import { ChangeDetectionStrategy, Component, inject, Inject, signal } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { ReportsEudrModel } from "@components/reports/eudr/reports-eudr.model";
import { IDeliveryExtended } from "@shared/interfaces";
import { EudrDds } from "@shared/interfaces/eudr-dds.interface";
import { AttachmentsService, AuthenticationService, NotificationService } from "@shared/services";
import { EudrDdsService } from "@shared/services/api/eudr-dds.service";
import { CustomValidators } from "@shared/validators";

@Component({
  selector: "app-add-dds-reference-dialog",
  templateUrl: "./add-dds-reference-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddDdsReferenceDialogComponent {
  private readonly authService = inject(AuthenticationService);

  private readonly eudrDdsService = inject(EudrDdsService);

  private readonly notificationService = inject(NotificationService);

  private readonly attachmentsService = inject(AttachmentsService);

  private readonly activeOrganisationId = this.authService.getActiveOrganisationId();

  public formGroup: UntypedFormGroup = new UntypedFormGroup({});

  isLoading = signal<boolean>(false);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      ddsReference: EudrDds.DdsReference;
      delivery: IDeliveryExtended;
    },
    public dialogRef: MatDialogRef<AddDdsReferenceDialogComponent>,
  ) {
    this.formGroup = new UntypedFormGroup({
      internalReferenceNumber: new UntypedFormControl(
        this.data?.ddsReference?.internalReferenceNumber,
        [CustomValidators.required],
      ),
    });
  }

  public readonly translations = {
    internalReferenceNumber: $localize`:@@addreferencedialog.referencenumber:Internal reference number`,
  };

  async onSubmit() {
    this.isLoading.set(true);
    try {
      const payload: EudrDds.IPayloadGenerated = {
        deliveryUri: `/organisations/${this.activeOrganisationId}/deliveries/${this.data?.delivery?.id}`,
        operatorType: "OPERATOR",
        type: EudrDds.TypeEnum.NEW,
        statement: {
          internalReferenceNumber: this.formGroup.controls["internalReferenceNumber"]?.value,
          activityType: null,
          countryOfActivity: this.data.delivery?.from?.address?.country,
          borderCrossCountry: this.data.delivery?.to?.address?.country,
          comment: "",
          commodities: null,
          geoLocationConfidential: false,
          associatedStatements: [],
        },
      };

      const ddsReference = await this.eudrDdsService.createOrUpdate(payload);

      await this.createDdsReferenceAttachment(ddsReference);

      this.notificationService.showSuccess(ReportsEudrModel.ddsSavedSuccessMessage);
      this.onClose(true, ddsReference);
    } catch (error) {
      this.notificationService.showError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  onClose(hasSaved = false, ddsReference: EudrDds.DdsReference = null) {
    this.dialogRef.close({ hasSaved, ddsReference });
  }

  private async createDdsReferenceAttachment(ddsReference: EudrDds.DdsReference): Promise<void> {
    await this.attachmentsService.create({
      targetUri: `/organisations/${this.activeOrganisationId}/deliveries/${this.data?.delivery?.id}`,
      attachmentUri: `/organisations/${this.activeOrganisationId}/extensions/eudr/references/${ddsReference?.id}`,
    });
  }

  public isSubmitButtonDisabled = (): boolean => this.formGroup?.invalid;
}
